import type { ModuleIdValue } from '@amumin/api-client';
import type { IconId } from '@amumin/ui-core';

import { relativeSeesAgenda } from '../state/care.js';
import { careOffered, moduleOn } from '../state/modules.js';
import { appText, context, messages } from '../state/session.js';
import { familyLike, householdType, houseWords, sharedHouse } from '../state/householdType.js';

// Navigatie van de ouderapp (WP2, docs/ux/compact-plan.md hoofdstuk 3; besluit oprichter 5 okt):
// vijf vaste plekken (Vandaag, Agenda, Taken, Eten, Meer), op de telefoon en de computer gelijk.
// Wat niet dagelijks is, staat onder Meer: op de computer als de rest van de zijbalk, op de
// telefoon als blad. Een paar losse pagina's zijn samengevoegd tot één pagina met tabbladen (een
// "hub"): Prikbord, Kinderen, Samen, Bord en Gezin. Elk tabblad houdt zijn bestaande adres, dus
// links in e-mails, pushberichten en de QR van het bord blijven werken. Sterren en Ritten zijn
// weergaven binnen Taken en Agenda (WP4). Dit bestand is alleen het model (geen JSX), te testen.

/** Eigen pictogrammen naast de sprite (zelfde lijnstijl; components/Shell.tsx tekent ze). */
export type NavIcon = IconId | 'board' | 'note' | 'frame' | 'kompas' | 'money';

/** Samengevoegde pagina's (hubs): één titel, tabbladen eronder (lib/hub.tsx). */
export type HubId = 'prikbord' | 'kinderen' | 'samen' | 'bord' | 'gezin';

export type NavKey =
  | 'today'
  | 'agenda'
  | 'tasks'
  | 'food'
  | 'pinboard'
  | 'children'
  | 'together'
  | 'money'
  | 'kompas'
  | 'myTodos'
  | 'boardHub'
  | 'members'
  | 'settings'
  // Tabbladen van de hubs.
  | 'rides'
  | 'stars'
  | 'rotation'
  | 'cleaning'
  | 'notes'
  | 'messages'
  | 'wishes'
  | 'learning'
  | 'growth'
  | 'school'
  | 'dinner'
  | 'costs'
  | 'bookings'
  | 'boardView'
  | 'devices'
  | 'rooms'
  | 'photos'
  | 'kidLock'
  | 'membersTab'
  | 'family'
  | 'circle'
  // Meekijker (golf 8) en platformbeheer.
  | 'myCircle'
  | 'notifications'
  | 'account'
  | 'beheer'
  | 'werklijst';

export interface NavItem {
  key: NavKey;
  href: string;
  icon: NavIcon;
  /** 0029: alleen als deze module aan staat (Instellingen, Modules). */
  module?: ModuleIdValue;
  /** Alleen voor ouders (Groei, Geldzaken, Kompas, Fotolijst, ...). */
  parentsOnly?: boolean;
  /** Een samengevoegde pagina: de plek wijst naar het eerste zichtbare tabblad. */
  hub?: HubId;
  /** Andere paden waarbij deze plek oplicht (de plek zelf en alles eronder telt altijd). */
  also?: ReadonlyArray<string>;
  /** Paden onder deze plek die toch bij een andere plek horen. */
  except?: ReadonlyArray<string>;
}

export interface HubDef {
  /** Sleutel van de paginatitel. */
  title: NavKey;
  /**
   * De tabbladen, elk op zijn bestaande adres: e-mails, pushberichten, de QR van het bord en
   * bladwijzers blijven zo werken, zonder doorverwijzing.
   */
  tabs: ReadonlyArray<NavItem>;
  /**
   * Tekent de schil (App.tsx, `hubbed`) de kop met tabbladen? Nee bij Prikbord en Kinderen (WP4,
   * notes/Pinboard.tsx en school/Kids.tsx) en bij Bord en Gezin (WP3, board/Hub.tsx): daar tekenen
   * de schermen hun tabbladen zelf. Alleen Samen krijgt de kop van de schil.
   */
  wrapped: boolean;
}

export const HUBS: Record<HubId, HubDef> = {
  prikbord: {
    title: 'pinboard',
    wrapped: false,
    tabs: [
      { key: 'notes', href: '/notities', icon: 'note', module: 'notes' },
      { key: 'messages', href: '/berichten', icon: 'mail', module: 'messages' },
      { key: 'wishes', href: '/wensen', icon: 'heart', module: 'wishes' },
    ],
  },
  kinderen: {
    title: 'children',
    wrapped: false,
    tabs: [
      { key: 'learning', href: '/leerdoelen', icon: 'book-open-text', module: 'memorize' },
      { key: 'growth', href: '/groei', icon: 'baby', module: 'health' },
      { key: 'school', href: '/school', icon: 'book-open', module: 'school' },
    ],
  },
  samen: {
    title: 'together',
    wrapped: true,
    tabs: [
      { key: 'dinner', href: '/wie-eet-mee', icon: 'cooking-pot', module: 'dinner_list' },
      { key: 'costs', href: '/kosten', icon: 'coins', module: 'expenses', parentsOnly: true },
      { key: 'bookings', href: '/reserveren', icon: 'calendar-clock', module: 'bookings' },
    ],
  },
  // Bord: Board, Tablets, Kamers, Fotolijst en Kindslot; WP3 tekent de tabbladen (board/Hub.tsx).
  bord: {
    title: 'boardHub',
    wrapped: false,
    tabs: [
      { key: 'boardView', href: '/board', icon: 'board', parentsOnly: true },
      { key: 'devices', href: '/apparaten', icon: 'tablet', also: ['/pair'] },
      { key: 'rooms', href: '/kamers', icon: 'house', module: 'rooms' },
      {
        key: 'photos',
        href: '/fotolijst',
        icon: 'frame',
        module: 'photo_frame',
        parentsOnly: true,
      },
      { key: 'kidLock', href: '/kindslot', icon: 'lock' },
    ],
  },
  // Gezin: Leden, Familie en Kring; WP3 tekent de tabbladen (board/Hub.tsx).
  gezin: {
    title: 'members',
    wrapped: false,
    tabs: [
      { key: 'membersTab', href: '/leden', icon: 'users' },
      { key: 'family', href: '/familie', icon: 'link' },
      { key: 'circle', href: '/instellingen/zorg', icon: 'circle-check', parentsOnly: true },
    ],
  },
};

/**
 * De vaste plekken vóór Meer, in volgorde. Restpunten (docs/ux/restpunten; onderzoek
 * professioneel-bord: "de zijbalk naar 6 hoofdpunten plus Meer"): de zijbalk toont de eerste vijf
 * die zichtbaar zijn plus Meer (zes punten), de onderbalk op de telefoon de eerste vier plus Meer
 * (vijf). Wat niet past, staat bovenaan in Meer. Alle oude adressen blijven werken.
 */
export const PRIMARY: ReadonlyArray<NavItem> = [
  { key: 'today', href: '/vandaag', icon: 'house' },
  // Ritten is een weergave in Agenda (WP4); /ritten blijft bestaan.
  { key: 'agenda', href: '/agenda', icon: 'calendar-days', also: ['/ritten'] },
  // Sterren (Beloningen en Goedkeuren) is een tabblad van Taken (WP4), Klusjes (Schoonmaak en
  // Rooster) een filter onder Alle; de adressen blijven.
  {
    key: 'tasks',
    href: '/taken',
    icon: 'list-checks',
    also: ['/beloningen', '/goedkeuren', '/schoonmaak', '/rooster'],
  },
  { key: 'food', href: '/eten', icon: 'utensils', module: 'food' },
  // Prikbord (notities, berichten, wensen): dagelijks genoeg voor de zijbalk; op de telefoon in Meer.
  { key: 'pinboard', href: '/notities', icon: 'note', hub: 'prikbord' },
];

/** Zoveel vaste plekken staan er vóór Meer in de zijbalk (computer) en de onderbalk (telefoon). */
export const SIDEBAR_FIXED = 5;
export const TABBAR_FIXED = 4;

/**
 * Meer, in drie groepen: thuis (Kinderen, Samen; op de telefoon ook Prikbord), Mijn omgeving
 * (Geldzaken, Kompas, Mijn to-do's; privé per volwassene, ook via de omgevingskiezer) en beheer
 * (Bord, Gezin, Instellingen). Rooster en Schoonmaak staan in Taken (filter Klusjes). Sinds de
 * restpunten staat Meer op de computer ook als knop in de zijbalk, met hetzelfde blad.
 */
export const MORE_GROUPS: ReadonlyArray<ReadonlyArray<NavItem>> = [
  [
    { key: 'children', href: '/leerdoelen', icon: 'baby', hub: 'kinderen' },
    { key: 'together', href: '/wie-eet-mee', icon: 'coins', hub: 'samen' },
  ],
  [
    { key: 'money', href: '/geldzaken', icon: 'money', module: 'money', parentsOnly: true },
    { key: 'kompas', href: '/kompas', icon: 'kompas', parentsOnly: true },
    // Oprichter 6 okt: je eigen to-do's (alleen jij ziet ze; pages/MyTodos.tsx).
    { key: 'myTodos', href: '/mijn-todo', icon: 'list-todo', parentsOnly: true },
  ],
  [
    { key: 'boardHub', href: '/board', icon: 'board', hub: 'bord' },
    { key: 'members', href: '/leden', icon: 'users', hub: 'gezin' },
    {
      key: 'settings',
      href: '/instellingen',
      icon: 'settings',
      also: ['/kalenders'],
      // Check-in en kring staat onder Gezin, ook al begint het adres met /instellingen.
      except: ['/instellingen/zorg'],
    },
  ],
];

/** Index van de groep Mijn omgeving in MORE_GROUPS (krijgt een eigen label). */
export const PERSONAL_GROUP = 1;

/**
 * Golf 8 (0050; §3.3.4): een meekijker (rol relative) ziet alleen Vandaag, Agenda (met het recht
 * 'agenda zien'), Mijn kring, Meldingen en Account. Verder niets: de API weigert de rest ook.
 */
export const RELATIVE_NAV: ReadonlyArray<NavItem> = [
  { key: 'today', href: '/vandaag', icon: 'house' },
  { key: 'agenda', href: '/agenda', icon: 'calendar-days' },
  { key: 'myCircle', href: '/kring', icon: 'users' },
  { key: 'notifications', href: '/instellingen/meldingen', icon: 'bell' },
  { key: 'account', href: '/instellingen/beveiliging', icon: 'user' },
];

/** Platformbeheerder (auth-context platform_admin): een eigen blokje onderaan. */
export const ADMIN_NAV: ReadonlyArray<NavItem> = [
  { key: 'beheer', href: '/beheer', icon: 'globe' },
  { key: 'werklijst', href: '/beheer/werklijst', icon: 'shield' },
];

/** De rol van deze login in het actieve huishouden. */
export function activeRole(): string | undefined {
  const ctx = context.value;
  return ctx?.households.find((x) => x.id === ctx.active_household_id)?.role;
}

/** 0053: is het actieve huishouden de eigen, persoonlijke omgeving? */
export function activePersonal(): boolean {
  const ctx = context.value;
  return ctx?.households.find((x) => x.id === ctx.active_household_id)?.personal === true;
}

/** Golf 8: is deze login in het actieve huishouden een meekijker? */
export function isRelative(): boolean {
  return activeRole() === 'relative';
}

/** Mag deze login deze plek of dit tabblad zien (module aan, rol, soort huishouden)? */
export function visible(item: NavItem): boolean {
  if (item.hub) return hubTabs(item.hub).length > 0;
  const role = activeRole();
  const parent = role === 'owner' || role === 'parent';
  if (item.module && !moduleOn(item.module)) return false;
  if (item.parentsOnly && !parent) return false;
  switch (item.key) {
    // Golf 8: Check-in en kring alleen als hij bij dit huishouden hoort.
    case 'circle':
      return careOffered();
    // Ronde 1 golf 8: een studentenhuis of huisgenoten hebben geen gezinskoppeling (Familie).
    case 'family':
      return !sharedHouse.value;
    // 0068: Kompas als de module hier aan staat, of in je eigen omgeving.
    case 'kompas':
      return moduleOn('kompas') || activePersonal();
    // Mijn to-do's: in het gezin altijd (de weg naar Mijn omgeving, waar de module standaard aan
    // staat); in Mijn omgeving zelf alleen met de module To-do's.
    case 'myTodos':
      return !activePersonal() || moduleOn('todos');
    default:
      return true;
  }
}

/** De zichtbare tabbladen van een hub, in vaste volgorde. */
export function hubTabs(id: HubId): NavItem[] {
  // Golf 8: een meekijker ziet alleen zijn eigen plekken (Agenda zonder Ritten).
  if (isRelative()) return HUBS[id].tabs.filter((t) => RELATIVE_NAV.some((r) => r.href === t.href));
  return HUBS[id].tabs.filter((t) => visible(t));
}

/** Waar een plek naartoe wijst: bij een hub het eerste zichtbare tabblad. */
export function itemHref(item: NavItem): string {
  if (!item.hub) return item.href;
  return hubTabs(item.hub)[0]?.href ?? item.href;
}

/** Ligt dit pad op of onder een van deze adressen? */
function under(path: string, bases: ReadonlyArray<string>): boolean {
  return bases.some((p) => path === p || path.startsWith(`${p}/`));
}

/** Licht deze plek op bij dit pad? Een hub licht op bij elk van zijn tabbladen. */
export function isActive(path: string, item: NavItem): boolean {
  // Beheer: alles onder /beheer behalve de werklijst (die heeft een eigen item).
  if (item.key === 'beheer') return under(path, ['/beheer']) && !under(path, ['/beheer/werklijst']);
  if (under(path, item.except ?? [])) return false;
  if (item.hub && activeTab(item.hub, path)) return true;
  return under(path, [item.href, ...(item.also ?? [])]);
}

/**
 * Het actieve tabblad van een hub: het langste adres dat past (zo wint /apparaten/virtueel bij
 * Tablets), ook via `also` (Koppelen hoort bij Tablets).
 */
export function activeTab(id: HubId, path: string): NavItem | undefined {
  let best: NavItem | undefined;
  let bestLen = -1;
  for (const t of HUBS[id].tabs) {
    for (const base of [t.href, ...(t.also ?? [])]) {
      if (under(path, [base]) && base.length > bestLen) {
        best = t;
        bestLen = base.length;
      }
    }
  }
  return best;
}

/** De onderbalk: de eerste vier vaste plekken (of die van een meekijker) plus Meer. */
export function tabbarItems(): NavItem[] {
  if (isRelative()) return relativeItems().filter((i) => i.key !== 'account');
  return PRIMARY.filter(visible).slice(0, TABBAR_FIXED);
}

/** De zijbalk: de eerste vijf vaste plekken plus Meer (een meekijker: zijn eigen lijst). */
export function sidebarItems(): NavItem[] {
  if (isRelative()) return relativeItems();
  return PRIMARY.filter(visible).slice(0, SIDEBAR_FIXED);
}

/** Golf 8: de navigatie van een meekijker (Agenda alleen met het recht 'agenda zien'). */
export function relativeItems(): NavItem[] {
  return RELATIVE_NAV.filter(
    (i) =>
      (i.key !== 'agenda' || relativeSeesAgenda.value) &&
      (i.key !== 'notifications' || moduleOn('push')),
  );
}

/**
 * De groepen onder Meer (een lege groep blijft staan, zodat PERSONAL_GROUP klopt). `fixed` = wat
 * daar al vast staat (onderbalk of zijbalk): vaste plekken die daar niet passen, komen vooraan in
 * de eerste groep.
 */
export function moreGroups(fixed: ReadonlyArray<NavItem> = tabbarItems()): NavItem[][] {
  const shown = new Set(fixed.map((i) => i.key));
  const rest = PRIMARY.filter((i) => visible(i) && !shown.has(i.key));
  return MORE_GROUPS.map((g, index) => {
    const list = g.filter(visible);
    return index === 0 ? rest.concat(list) : list;
  });
}

/** De tekst van een plek of tabblad, in de taal van de app. */
export function navLabel(key: NavKey): string {
  const a = appText.value;
  const m = messages.value;
  switch (key) {
    case 'food':
      return m.food.nav;
    case 'wishes':
    case 'messages':
    case 'family':
      return m.family.nav[key];
    case 'rooms':
      return m.kamers.nav.rooms;
    case 'kidLock':
      return m.kamers.nav.kidLock;
    case 'cleaning':
      // Golf 6b: bij een gezin zoals altijd; andere soorten hun eigen woord ("Rooster").
      return familyLike.value ? m.kamers.nav.cleaning : houseWords.value.cleaning;
    case 'rotation':
      return a.households.rotation.title;
    case 'learning':
      return a.memorize.nav;
    case 'dinner':
      return householdType.value === 'cohousing' ? a.samen.nav.dinnerCohousing : a.samen.nav.dinner;
    case 'bookings':
      return a.samen.nav.bookings;
    case 'costs':
      return a.samen.nav.costs;
    case 'tasks':
      return familyLike.value ? a.nav.tasks : houseWords.value.tasks;
    case 'members':
      return familyLike.value ? a.nav.members : houseWords.value.members;
    case 'membersTab':
      return familyLike.value ? a.nav.membersTab : houseWords.value.members;
    case 'myCircle':
      return a.care.myCircleTitle;
    case 'notifications':
      return a.push.title;
    case 'account':
      return a.care.account;
    default:
      return a.nav[key];
  }
}
